// Angular
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';

// Libraries
import { takeUntil } from 'rxjs';
import * as objectPath from 'object-path';

// Models
import { AppCleanup } from '../../../../../../shared/models';
import { JobData, JobDataPoint, UserUnitPeriodChannelValue } from '../../../../models';
import { JobChange, JobUpdate } from '../../../../models/job.model';
import { ChannelViewModel } from '../../../../../unit-config/models/unit-config-view.model';
import { UnitDetailViewModel } from '../../../../../asset/unit/models/unit-detail-view.model';

// Services
import { JobService } from '../../../../services/job.service';
import { UnitService } from '../../../../../asset/unit/services/unit.service';
import { UnitConfigService } from '../../../../../unit-config/services/unit-config.service';
import { CoreService } from '../../../../../../shared/services';
import { LayoutConfigService } from '../../../../../../shared/services/layout-config.service';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '../../../../../../shared/services/dialog.service';

// Components
import { CalibrationHelpDialogComponent } from '../calibration-help-dialog/calibration-help-dialog.component';
import { ConfirmDialogModel } from '../../../../../../shared/dialogs/confirm-dialog/confirm-dialog.component';

// Enums
import { StatusType } from '../../../../../../shared/enums';

// Helpers
import { UtilityHelper } from '../../../../../../shared/helpers';

@Component({
	selector: 'calibration-dialog',
	templateUrl: './calibration-dialog.component.html',
	styleUrls: ['./calibration-dialog.component.scss'],
})
export class CalibrationDialogComponent extends AppCleanup implements OnInit, OnDestroy {
	componentTitle: string;
	private hasCalculateBeenClicked: boolean = false;
	private newOffset: number = null;
	private newGain: number = null;
	private unitConfigId: number = null;
	private unitGuid: string = null;
	private unitId: number = null;
	private jobKey: string = null;

	readonly gainOffsetPrecision: number = 4;
	channelName: string = '';
	channelNumber: number = null;
	precision: number = null;
	userChannelOffset: number = null;
	currentOffset: number = null;
	currentGain: number = null;
	physicalReading1: number = null;
	physicalReading2: number = null;
	sensorReading1: number = null;
	sensorReading2: number = null;
	currentReading: number = null;
	adjustedReading: number = null;
	shouldShowHttpError: boolean = false;
	shouldShowApplyConfirmation: boolean = false;
	showSpinner: boolean = false;
	primaryColor: string;

	constructor(
		public dialogRef: MatDialogRef<CalibrationDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: { userJobChannelValue: UserUnitPeriodChannelValue; key: string },
		private dialog: MatDialog,
		private layoutConfigService: LayoutConfigService,
		private jobService: JobService,
		private coreService: CoreService,
		private unitService: UnitService,
		private unitConfigService: UnitConfigService,
		private translateService: TranslateService,
		private dialogService: DialogService
	) {
		super();
	}

	ngOnInit() {
		// Initialize local data.
		const channel = this.data.userJobChannelValue.userUnitPeriodChannel.unitPeriodChannel;
		this.channelName = channel.channel.label;
		this.channelNumber = channel.channelId;
		this.precision = channel.channel.precision || 0;
		this.unitGuid = channel.unit.guid;
		this.unitId = channel.unit.id;
		this.componentTitle = `Calibration tool - ${this.channelName}`;

		// this.jobService.jobChange$.pipe(takeUntil(this.destroy)).subscribe((jobChange) => this.processJobChange(jobChange));

		this.jobService
			.getJobStream(String(this.data.key))
			.pipe(takeUntil(this.destroy))
			.subscribe((jobUpdate: JobUpdate) => {
				if (jobUpdate) {
					this.processJobChange(jobUpdate);
				}
			});

		this.jobService.newDataSent$.pipe(takeUntil(this.destroy)).subscribe((jobDataList: JobData[]) => this.processUpdatedJobData(jobDataList));
	}

	ngOnDestroy() {
		this.channelName = '';
		this.channelNumber = null;
		this.precision = null;
		this.currentOffset = null;
		this.currentGain = null;
		this.physicalReading1 = null;
		this.physicalReading2 = null;
		this.sensorReading1 = null;
		this.sensorReading2 = null;
		this.currentReading = null;
		this.adjustedReading = null;
		this.hasCalculateBeenClicked = false;
		this.newOffset = null;
		this.newGain = null;
		this.shouldShowHttpError = false;
		this.shouldShowApplyConfirmation = false;

		super.ngOnDestroy();
	}

	get getCurrentGain(): string {
		return this.getAdjustedValueForDisplay(this.currentGain, this.gainOffsetPrecision);
	}

	get getCurrentOffset(): string {
		return this.getAdjustedValueForDisplay(this.currentOffset, this.gainOffsetPrecision);
	}

	get getAdjustedGain(): string {
		return this.getAdjustedValueForDisplay(this.newGain, this.gainOffsetPrecision);
	}

	get getAdjustedOffset(): string {
		return this.getAdjustedValueForDisplay(this.newOffset, this.gainOffsetPrecision);
	}

	get getCurrentReading(): string {
		return this.getAdjustedValueForDisplay(this.currentReading, null, this.userChannelOffset);
	}

	get getAdjustedReading(): string {
		return this.getAdjustedValueForDisplay(this.adjustedReading);
	}

	changeField() {
		this.hasCalculateBeenClicked = false;
		this.newGain = null;
		this.newOffset = null;
		this.adjustedReading = null;
	}

	readyToSave(): boolean {
		return this.adjustedReading !== null;
	}

	areEntriesValid(): boolean {
		const areValuesSet: boolean = [this.physicalReading1, this.physicalReading2, this.sensorReading1, this.sensorReading2].every((entry: number) => entry != null);
		const areSensorReadingsIdentical = this.sensorReading1 === this.sensorReading2;

		if (!areValuesSet || areSensorReadingsIdentical) {
			this.adjustedReading = null;
		}

		return areValuesSet && !areSensorReadingsIdentical;
	}

	calculateCalibrationClick() {
		if (!this.areEntriesValid()) return;
		this.calculateCalibration();
	}

	processJobChange(jobUpdate: JobUpdate) {
		this.jobKey = jobUpdate.job.key;
		this.initializeUserChannelOffset(this.jobKey);
		this.initializeDataByUnitId(this.unitId);
	}

	initializeUserChannelOffset(jobKey: string) {
		const channelOffsets = this.jobService.findUnitPeriodChannelOffsetsForJob(jobKey);
		let unitPeriodChannelOffsets = channelOffsets.filter((dto) => dto.unitPeriodChannel.channelId === this.data.userJobChannelValue.userUnitPeriodChannel.unitPeriodChannel.channelId);

		let channelOffset = 0;
		if (unitPeriodChannelOffsets.length > 0) {
			const lastIndex = unitPeriodChannelOffsets.length - 1;
			channelOffset = unitPeriodChannelOffsets[lastIndex].offsetValue;
		}

		this.userChannelOffset = channelOffset;
	}

	initializeDataByUnitId(unitId: number) {
		if (unitId)
			this.unitService.getUnit(unitId).subscribe({
				next: (unitDetail: UnitDetailViewModel) => {
					const channel = unitDetail.unit.unitConfig.channels.find((channel: ChannelViewModel) => channel.name === this.channelName);

					this.currentGain = channel.gain;
					this.currentOffset = channel.offset;
					this.unitConfigId = unitDetail.unit.unitConfig.id;
				},
			});
	}

	processUpdatedJobData(jobDataList: JobData[]) {
		jobDataList = this.jobService.applyOffsetsToDataPoints(this.data.key, jobDataList);
		jobDataList = this.jobService.applyPrecisionToDataPoints(this.data.key, jobDataList);

		let jobData: JobData = jobDataList && jobDataList.length > 0 ? jobDataList[0] : null;

		if (jobData && this.data.key === jobData.k) {
			let channelValue = jobData.c.find((point: JobDataPoint) => point.i === this.channelNumber);

			if (channelValue) {
				this.currentReading = channelValue.rawValue;

				if (this.areEntriesValid() && this.hasCalculateBeenClicked) this.calculateCalibration();

				if (!this.hasCalculateBeenClicked) {
					this.newGain = null;
					this.newOffset = null;
					this.adjustedReading = null;
				}
			}
		}
	}

	openHelpDialog() {
		const dialogRef = this.dialog.open(CalibrationHelpDialogComponent, {
			width: '100%',
			maxWidth: '600px',
			autoFocus: false,
		});
	}

	openWarningDialog() {
		const confirmModel: ConfirmDialogModel = {
			title: 'Attention',
			message: 'Please revisit your entries to ensure you have entered valid combinations',
			hideAction: true,
			cancelText: 'Close',
		};

		const dialogRef = this.dialogService.confirmAction(confirmModel);
	}

	submit() {
		const confirmModel: ConfirmDialogModel = {
			title: 'Apply calibration',
			htmlMessage: 'Are you sure you want to apply this configuration change? <br/> <b>Please Note:</b> Taking this action will restart the connection to the unit which may take several seconds',
		};

		const dialogRef = this.dialogService.confirmAction(confirmModel);
		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result) {
					// Hide confirmation dialog.
					this.shouldShowApplyConfirmation = false;

					// Show loading indicator.
					this.showSpinner = true;

					// HTTP call to update the unit configuration.
					const dto = {
						id: this.unitConfigId,
						channelId: this.channelName,
						offset: this.newOffset,
						gain: this.newGain,
					};
					this.unitConfigService
						.UpdateUnitConfigurationOffsetAndGain(dto)
						.pipe(takeUntil(this.destroy))
						.subscribe({
							next: async () => {
								// Add comment.
								this.addComment();
								// Display toast notification.
								await UtilityHelper.sleep(1000);

								const message = `${this.channelName} channel calibration change successful; re-starting unit momentarily...`;
								this.coreService.toast({ message: message, type: StatusType.SUCCESS });

								// Exit modal & restart unit.
								this.dialogRef.close({
									unitId: this.unitId,
									channelName: this.channelName,
								});
							},
							error: async () => {
								await UtilityHelper.sleep(350);
								this.showSpinner = false;
								const message = 'An HTTP error occurred when trying to save the changes to the channel configuration. Please try to save again and contact support if this issue persists';
								this.coreService.toast({ message: message, type: StatusType.DANGER });
							},
						});
				}
			});
	}

	closeError() {
		this.shouldShowHttpError = false;
	}

	closeApplyConfirmation() {
		this.shouldShowApplyConfirmation = false;
	}

	cancelConfirmation() {
		this.shouldShowApplyConfirmation = false;
	}

	private addComment() {
		this.jobService.addJobComment(this.jobKey, {
			id: 0,
			jobId: this.jobService.currentJob.id,
			comment: this.buildComment(),
			commentDate: new Date(),
			jobCommentDefaultId: 0,
			replacements: '',
			hideInPortal: false,
		});
	}

	private buildComment(): string {
		const currentGain: string = this.getAdjustedValueForDisplay(this.currentGain, this.gainOffsetPrecision);
		const currentOffset: string = this.getAdjustedValueForDisplay(this.currentOffset, this.gainOffsetPrecision);
		const newGain = this.getAdjustedGain;
		const newOffset = this.getAdjustedOffset;

		return `${this.channelName} Channel Calibration Change - Gain changed from: ${currentGain} to: ${newGain}, Offset changed from: ${currentOffset} to ${newOffset}`;
	}

	private calculateCalibration() {
		const appliedOffset = this.userChannelOffset ? this.currentOffset + this.userChannelOffset : this.currentOffset;

		// Remove current calibration to get raw sensor readings
		const x1Raw = (this.sensorReading1 - appliedOffset) / this.currentGain;
		const x2Raw = (this.sensorReading2 - appliedOffset) / this.currentGain;

		// Calculate new gain and offset.
		const newGain = (this.physicalReading2 - this.physicalReading1) / (x2Raw - x1Raw);
		const newOffset = this.physicalReading1 - newGain * x1Raw;
		// Ensure that all calculated values are real, proper numerics.
		// Otherwise, display warning message to user.
		if (this.areValuesNanOrInfinite(newGain, newOffset)) {
			this.hasCalculateBeenClicked = false;
			this.newGain = null;
			this.newOffset = null;
			this.adjustedReading = null;
			this.openWarningDialog();
		} else {
			this.hasCalculateBeenClicked = true;

			// Store newly calculated gain and offset; account for user set offset in other modal.
			this.newGain = newGain;
			this.newOffset = newOffset;

			// Update the adjusted reading based on the new gain and offset; account for user set offset in other modal.
			const rawSenorReading = (this.currentReading - appliedOffset) / this.currentGain;
			const adjustedReading = rawSenorReading * newGain + newOffset;
			this.adjustedReading = this.userChannelOffset ? adjustedReading + this.userChannelOffset : adjustedReading;
		}
	}

	private getAdjustedValueForDisplay(value: number, initialPrecision = null, offset = null): string {
		if (value === null) return `--`;

		const precision = this.newOffset > 1 ? initialPrecision || this.precision : this.gainOffsetPrecision;
		const options = {
			minimumFractionDigits: precision,
			maximumFractionDigits: precision,
		};

		// Apply offset, if set.
		if (offset) value = value + offset;

		return value.toLocaleString('en-US', options);
	}

	private areValuesNanOrInfinite(newGain: number, newOffset: number): boolean {
		return Number.isNaN(newGain) || Number.isNaN(newOffset) || this.isInfinite(newGain) || this.isInfinite(newOffset);
	}

	private isInfinite(value: number): boolean {
		return value === Infinity || value === -Infinity;
	}
}
